import Fernet from 'fernet';
import axios from 'axios';
import { ACS_API_BASE, ACS_ENDPOINTS } from './acs';

const FERNET_KEY = '8y4Ia5t_jQpDr_PNlpSvye8Dmeft1HwnsC1OadsDRaI=';
const AUTO_LOGIN_URL = 'https://console.ffeleven.com/administration/num_reporting/auto_login';

const createLoginToken = (username, password) => {
  const secret = new Fernet.Secret(FERNET_KEY);
  const token = new Fernet.Token({
    secret: secret,
    time: Date.now(),
  });
  return token.encode([username, password].join(':'));
};

const toBooleanFlag = (value) => {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return undefined;
};

const extractSuperUser = (data) => {
  const payload = data && data.payload;
  return toBooleanFlag(payload && payload.is_superuser);
};

const persistSuperUser = (data) => {
  const isSuper = extractSuperUser(data);
  if (isSuper === undefined) {
    localStorage.removeItem('is_superuser');
    return null;
  }
  localStorage.setItem('is_superuser', isSuper ? 'true' : 'false');
  return isSuper;
};

const PASSWORD_STORAGE_KEY = 'console_password';
const PASSWORD_PREFIX = 'enc:';

const bytesToBase64 = (bytes) => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const base64ToBytes = (value) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const getPasswordKey = async () => {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode('adstraffic-admin-console-password'),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new TextEncoder().encode('console-password'),
      iterations: 100000,
      hash: 'SHA-256',
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

export const saveConsolePassword = async (password) => {
  const key = await getPasswordKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(password)
  ));
  const packed = new Uint8Array(iv.length + cipher.length);
  packed.set(iv, 0);
  packed.set(cipher, iv.length);
  localStorage.setItem(PASSWORD_STORAGE_KEY, `${PASSWORD_PREFIX}${bytesToBase64(packed)}`);
};

export const readConsolePassword = async () => {
  const stored = localStorage.getItem(PASSWORD_STORAGE_KEY);
  if (!stored) return '';
  if (!stored.startsWith(PASSWORD_PREFIX)) {
    try {
      await saveConsolePassword(stored);
    } catch (error) {
      console.error('Failed to hide the saved console password:', error);
    }
    return stored;
  }
  try {
    const packed = base64ToBytes(stored.slice(PASSWORD_PREFIX.length));
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: packed.slice(0, 12) },
      await getPasswordKey(),
      packed.slice(12)
    );
    return new TextDecoder().decode(plain);
  } catch (error) {
    console.error('Failed to read the saved console password:', error);
    return '';
  }
};

readConsolePassword().catch(() => {});

const autoLogin = async (username, password, loginUrl = AUTO_LOGIN_URL) => {
  const encryptedMessage = createLoginToken(username, password);
  const response = await axios.get(`${loginUrl}?token=${encryptedMessage}`, {
    withCredentials: true,
  });
  const data = response.data || {};
  return {
    success: data.status === 'SUCCESS',
    data,
  };
};

const getAutoLoginUrlForAdmin = (url) => {
  try {
    return `${new URL(url).origin}/administration/num_reporting/auto_login`;
  } catch (error) {
    return AUTO_LOGIN_URL;
  }
};

export const isSuperUser = () => localStorage.getItem('is_superuser') === 'true';

export const getSuperUserFlag = () => {
  const value = localStorage.getItem('is_superuser');
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

export const refreshConsoleSession = async () => {
  const username = localStorage.getItem('username');
  const password = await readConsolePassword();
  if (!username || !password) {
    return false;
  }
  try {
    const result = await autoLogin(username, password);
    if (result.success) {
      persistSuperUser(result.data);
    }
    return result.success;
  } catch (error) {
    console.error('Failed to refresh console session:', error);
    return false;
  }
};

export const loginAcs = async (username, password) => {
  if (!username || !password) {
    return false;
  }
  const response = await axios.post(
    `${ACS_API_BASE}${ACS_ENDPOINTS.login}`,
    { username, password },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  );
  return Boolean(response.data && response.data.ok);
};

export const openConsoleAdmin = async (url) => {
  const popup = window.open('about:blank', '_blank');
  const username = localStorage.getItem('username');
  const password = await readConsolePassword();
  if (username && password) {
    try {
      await autoLogin(username, password, getAutoLoginUrlForAdmin(url));
    } catch (error) {
      console.error('Failed to refresh admin session:', error);
    }
  }
  if (popup && !popup.closed) {
    popup.opener = null;
    popup.location.href = url;
    return;
  }
  window.location.href = url;
};

export const authProvider = {
    login: async ({ username, password }) => {
      
      const handlelogin = async()=>{
        try {
          return await autoLogin(username, password);
        } catch (error) {
          console.error('Network or fetch error:', error);
          return { success: false, data: null };
        }
      }

      const result = await handlelogin();

      if (result.success) {
        localStorage.setItem('auth', 'true');
        localStorage.setItem('username',username);
        await saveConsolePassword(password);
        persistSuperUser(result.data);
        try {
          await loginAcs(username, password);
        } catch (error) {
          console.error('Failed to sign in to ACS admin:', error);
        }
        return Promise.resolve();
      }else{
        return Promise.reject('Invalid credentials');
      } 
    },
    logout: () => {
      if(localStorage.getItem('auth') && localStorage.getItem("username")){
        localStorage.clear();
        return Promise.resolve();
      }
      return Promise.reject('Error in logging out !');
    },
    checkAuth: () => {
      return localStorage.getItem('auth') ? Promise.resolve() : Promise.reject();
    },
    checkError: (error) => Promise.reject(error),
    getPermissions: () => Promise.resolve(getSuperUserFlag()),
  };
